import { Alert, Badge, Box, Button, Group, Modal, Radio, Stack, Text } from '@mantine/core';
import { format, parseISO } from 'date-fns';
import { useState, useEffect } from 'react';
import { primaryColor } from '../../constants/theme';
import { AvailableTime } from '../../types/AvailabilityModels';
import {
  buildDeleteOptions,
  DeleteOption,
  DeleteOptionDetail,
} from './deleteAvailabilityOptions';

interface DeleteAvailabilityModalProps {
  opened: boolean;
  slot: AvailableTime | null;
  allSlots: AvailableTime[];
  bookedIds?: Set<number>;
  onClose: () => void;
  onConfirm: (option: DeleteOption, affectedIds: number[]) => Promise<void>;
}

export const DeleteAvailabilityModal = ({
  opened,
  slot,
  allSlots,
  bookedIds,
  onClose,
  onConfirm,
}: DeleteAvailabilityModalProps) => {
  const [selected, setSelected] = useState<DeleteOption>('this-occurrence');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setSelected('this-occurrence');
      setError(null);
      setIsLoading(false);
    }
  }, [opened]);

  if (!slot) return null;

  const topicColour = slot.topics?.[0]?.colour ?? primaryColor;
  const topicName = slot.topics?.length
    ? slot.topics.map((t) => t.title).join(', ')
    : 'Open Session';
  const startLabel = format(parseISO(slot.startIso), 'HH:mm');
  const endLabel = format(parseISO(slot.endIso), 'HH:mm');

  const options: DeleteOptionDetail[] = buildDeleteOptions(slot, allSlots, bookedIds);
  const selectedOption = options.find((o) => o.option === selected)!;

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onConfirm(selectedOption.option, selectedOption.affectedIds);
    } catch {
      setError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Delete Availability"
      centered
      size="md"
      closeOnClickOutside={!isLoading}
      withCloseButton={!isLoading}
      data-testid="delete-availability-modal"
    >
      <Stack gap="md">
        {/* Slot preview */}
        <Group
          wrap="nowrap"
          align="center"
          justify="space-between"
          p="sm"
          style={{
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
          data-testid="delete-modal-slot-preview"
        >
          <Group wrap="nowrap" gap="sm" align="center">
            <Box
              style={{
                width: 6,
                minWidth: 6,
                height: 36,
                borderRadius: 3,
                backgroundColor: topicColour,
              }}
              data-testid="delete-modal-slot-colour"
            />
            <Text fw={600} size="sm" data-testid="delete-modal-topic-name">
              {topicName}
            </Text>
          </Group>
          <Text size="sm" c="dimmed" data-testid="delete-modal-slot-time">
            {startLabel} – {endLabel}
          </Text>
        </Group>

        {/* Booking exclusion notice */}
        <Group
          gap="xs"
          align="center"
          p="sm"
          style={{
            backgroundColor: `${primaryColor}18`,
            borderRadius: 'var(--mantine-radius-md)',
          }}
          data-testid="delete-modal-booking-notice"
        >
          <Text size="xs" style={{ color: primaryColor }}>
            Slot counts exclude any slots with confirmed or pending bookings. Those slots will not be deleted.
          </Text>
        </Group>

        {/* Delete scope options */}
        <Radio.Group
          value={selected}
          onChange={(val) => setSelected(val as DeleteOption)}
          data-testid="delete-modal-options"
        >
          <Stack gap="xs">
            {options.map((opt) => (
              <Group
                key={opt.option}
                wrap="nowrap"
                align="center"
                justify="space-between"
                p="sm"
                style={{
                  border: `1px solid ${selected === opt.option ? primaryColor : 'var(--mantine-color-gray-3)'}`,
                  borderRadius: 'var(--mantine-radius-md)',
                  cursor: 'pointer',
                }}
                onClick={() => setSelected(opt.option)}
                data-testid={`delete-option-${opt.option}`}
              >
                <Radio
                  value={opt.option}
                  label={opt.label}
                  color={primaryColor}
                  styles={{ label: { cursor: 'pointer' } }}
                />
                <Badge
                  radius="sm"
                  variant="light"
                  color={selected === opt.option ? primaryColor : 'gray'}
                  data-testid={`delete-option-count-${opt.option}`}
                >
                  {opt.affectedIds.length} {opt.affectedIds.length === 1 ? 'slot' : 'slots'}
                </Badge>
              </Group>
            ))}
          </Stack>
        </Radio.Group>

        {/* Error alert */}
        {error && (
          <Alert color="red" data-testid="delete-modal-error">
            {error}
          </Alert>
        )}

        {/* CTAs */}
        <Group justify="flex-end" mt="md" grow>
          <Button
            variant="default"
            onClick={onClose}
            disabled={isLoading}
            data-testid="delete-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            color={primaryColor}
            onClick={() => void handleConfirm()}
            loading={isLoading}
            data-testid="delete-modal-confirm"
          >
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
